/**
 * Order and Invoice Constants
 * Centralized status definitions for professional and consistent display
 */

// Order Status Constants
export const ORDER_STATUS = {
    DRAFT: { value: 'draft', label: 'Draft', color: 'gray', bgClass: 'bg-gray-100 text-gray-800' },
    PENDING: { value: 'pending', label: 'Pending Review', color: 'yellow', bgClass: 'bg-yellow-100 text-yellow-800' },
    QUOTE_SENT: { value: 'quote_sent', label: 'Quote Sent', color: 'blue', bgClass: 'bg-blue-100 text-blue-800' },
    APPROVED: { value: 'approved', label: 'Approved', color: 'green', bgClass: 'bg-green-100 text-green-800' },
    PROCESSING: { value: 'processing', label: 'In Progress', color: 'purple', bgClass: 'bg-purple-100 text-purple-800' },
    REVIEW: { value: 'review', label: 'Under Review', color: 'orange', bgClass: 'bg-orange-100 text-orange-800' },
    COMPLETED: { value: 'completed', label: 'Completed', color: 'green', bgClass: 'bg-green-100 text-green-800' },
    CANCELLED: { value: 'cancelled', label: 'Cancelled', color: 'red', bgClass: 'bg-red-100 text-red-800' }
}

// Payment Status Constants
export const PAYMENT_STATUS = {
    UNPAID: { value: 'unpaid', label: 'Unpaid', color: 'red', bgClass: 'bg-red-100 text-red-800' },
    PARTIAL: { value: 'partial', label: 'Partially Paid', color: 'orange', bgClass: 'bg-orange-100 text-orange-800' },
    PAID: { value: 'paid', label: 'Paid', color: 'green', bgClass: 'bg-green-100 text-green-800' }
}

// Currency Configuration
export const CURRENCY = {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar'
}

// Helper function to get order status by value
export const getOrderStatusByValue = (value) => {
    return Object.values(ORDER_STATUS).find(status => status.value === value) || ORDER_STATUS.DRAFT
}

// Helper function to get payment status by value
export const getPaymentStatusByValue = (value) => {
    return Object.values(PAYMENT_STATUS).find(status => status.value === value) || PAYMENT_STATUS.UNPAID
}

// Helper function to calculate payment status based on amounts
export const calculatePaymentStatus = (totalAmount, paidAmount) => {
    const total = parseFloat(totalAmount) || 0
    const paid = parseFloat(paidAmount) || 0

    if (paid <= 0) return PAYMENT_STATUS.UNPAID.value
    if (paid >= total) return PAYMENT_STATUS.PAID.value
    return PAYMENT_STATUS.PARTIAL.value
}

// Format currency amount
export const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0
    return `${CURRENCY.symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Format currency amount without decimals (for display)
export const formatCurrencyShort = (amount) => {
    const num = parseFloat(amount) || 0
    return `${CURRENCY.symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

// Order status list for dropdowns
export const ORDER_STATUS_LIST = Object.values(ORDER_STATUS)

// Payment status list for dropdowns
export const PAYMENT_STATUS_LIST = Object.values(PAYMENT_STATUS)

// Progress percentage by status
export const getProgressByStatus = (statusValue) => {
    const progressMap = {
        'draft': 5,
        'pending': 15,
        'quote_sent': 30,
        'approved': 45,
        'processing': 70,
        'review': 85,
        'completed': 100,
        'cancelled': 0
    }
    return progressMap[statusValue] || 0
}

export default {
    ORDER_STATUS,
    PAYMENT_STATUS,
    CURRENCY,
    ORDER_STATUS_LIST,
    PAYMENT_STATUS_LIST,
    getOrderStatusByValue,
    getPaymentStatusByValue,
    calculatePaymentStatus,
    formatCurrency,
    formatCurrencyShort,
    getProgressByStatus
}
